import { z } from "zod";
import type { CallEvent, CallRow, CallStatus } from "./types";

/** The list filter: every call, or one kind of ending. */
export const CALL_FILTERS = ["answered", "missed", "failed"] as const;
export type CallFilter = (typeof CALL_FILTERS)[number];

export const FILTER_LABEL: Record<CallFilter, string> = {
	answered: "Answered",
	missed: "Missed",
	failed: "Failed",
};

const FILTER_STATUS: Record<CallFilter, CallStatus> = {
	answered: "ENDED",
	missed: "MISSED",
	failed: "FAILED",
};

export const callsSearchSchema = z.object({
	show: z.enum(CALL_FILTERS).optional(),
	q: z.string().optional(),
	/** The call open in the detail pane. */
	id: z.string().optional(),
	/** Detail tab. */
	tab: z.enum(["conversation", "products", "technical"]).optional(),
});
export type CallsSearch = z.infer<typeof callsSearchSchema>;

export function listInputFrom(organizationId: string, search: CallsSearch) {
	return {
		organizationId,
		...(search.show ? { status: FILTER_STATUS[search.show] } : {}),
		...(search.q ? { q: search.q } : {}),
	};
}

type BadgeVariant =
	| "secondary"
	| "outline"
	| "success"
	| "warning"
	| "destructive"
	| "info";

export const CALL_STATUS_META: Record<
	CallStatus,
	{ label: string; variant: BadgeVariant }
> = {
	RINGING: { label: "Ringing", variant: "info" },
	CONNECTING: { label: "Connecting", variant: "info" },
	LIVE: { label: "Live", variant: "success" },
	ENDED: { label: "Answered", variant: "secondary" },
	MISSED: { label: "Missed", variant: "warning" },
	REJECTED: { label: "Rejected", variant: "warning" },
	FAILED: { label: "Failed", variant: "destructive" },
};

export const isLive = (status: CallStatus) =>
	status === "RINGING" || status === "CONNECTING" || status === "LIVE";

/** Why a call ended, in the words a shift lead uses. */
export const END_REASON_LABEL: Record<string, string> = {
	caller_hangup: "Caller hung up",
	agent_ended: "Agent ended the call",
	staff_ended: "Your team ended the call",
	max_duration: "Reached the time limit",
	silence: "Caller went quiet",
	over_capacity: "Too many calls at once",
	spend_cap: "Spending limit reached",
	calling_off: "Calls were turned off",
	after_hours: "Outside call hours",
	connect_failed: "Couldn't connect the audio",
	voice_unavailable: "Call service unavailable",
	meta_failed: "WhatsApp refused the call",
	draining: "Call service restarting",
};

/** "4:12", "1:02:09". */
export function clockDuration(totalSeconds: number): string {
	const seconds = Math.max(0, Math.round(totalSeconds));
	const h = Math.floor(seconds / 3600);
	const m = Math.floor((seconds % 3600) / 60);
	const s = seconds % 60;
	const pad = (n: number) => String(n).padStart(2, "0");
	return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
}

export const callerLabel = (
	call: Pick<CallRow, "callerName" | "callerPhone">,
) => call.callerName ?? call.callerPhone ?? "Unknown caller";

export const EVENT_LABEL: Record<string, string> = {
	webhook_received: "Call arrived",
	forwarded: "Sent to the call service",
	pre_accept: "Pre-accepted",
	ice_connected: "Network connected",
	dtls_connected: "Audio encrypted",
	accept: "Accepted",
	first_audio: "First audio",
	terminate_sent: "Hang-up sent",
	terminate_webhook: "Hang-up confirmed",
	error: "Error",
};

/** Each step with its gap from the one before, the way the timeline reads ("+412 ms"). */
export function withDeltas(events: readonly CallEvent[]) {
	let previous: number | null = null;
	return events.map((event) => {
		const delta =
			event.ms !== null && previous !== null ? event.ms - previous : null;
		if (event.ms !== null) {
			previous = event.ms;
		}
		return { ...event, delta };
	});
}

/**
 * Where a call message sits in the recording. A stored `callOffsetMs` wins; otherwise the
 * gap from when the call was answered, which is what the recording starts at.
 */
export function offsetOf(
	message: { createdAt: Date | string; callOffsetMs?: number | null },
	answeredAt: Date | string | null,
): number | null {
	if (typeof message.callOffsetMs === "number") {
		return message.callOffsetMs;
	}
	if (!answeredAt) {
		return null;
	}
	return Math.max(
		0,
		new Date(message.createdAt).getTime() - new Date(answeredAt).getTime(),
	);
}

/** The message that is playing at `ms`: the last one that started at or before it. */
export function activeIndex(offsets: readonly (number | null)[], ms: number) {
	let active = -1;
	offsets.forEach((offset, index) => {
		if (offset !== null && offset <= ms) {
			active = index;
		}
	});
	return active;
}

/** "Today", "Yesterday" or the date, for the list's day groups. */
export function dayGroup(daysAgo: number, dateLabel: string): string {
	return daysAgo === 0 ? "Today" : daysAgo === 1 ? "Yesterday" : dateLabel;
}

export const usdCost = (value: number) =>
	value === 0 ? "$0" : value < 0.01 ? "<$0.01" : `$${value.toFixed(2)}`;
